import type { ApolloError } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import { type FC, useRef, useState } from "react";
import type { SubmitHandler } from "react-hook-form";
import {
  ActionButtonIntent,
  AlertDialogComponent,
  ColorButton,
  HStack,
  Popover,
  TextField,
  TriggerButton,
  VisuallyHidden,
  VStack,
} from "widgets";
import formStyle from "widgets/form.module.css";
import { useFormAria, useOverlay } from "widgets/hooks";
import { object, string, type z } from "zod";

import type { InputItemTagInput, ItemTag } from "../api/graphql.ts";

export type ItemTagUpdateData = Pick<InputItemTagInput, "name" | "colour">;

export interface TagListItemProps {
  /**
   * Optional. If no tag is given, the item is empty and in 'creation' mode
   */
  tag?: Pick<ItemTag, "name" | "color">;
  /**
   * Callback for creating a new tag (only for creation mode)
   */
  onCreate?: (tagData: ItemTagUpdateData) => Promise<void>;
  /**
   * Callback for updating an existing tag (only for edit mode)
   */
  onUpdate?: (tagData: ItemTagUpdateData) => Promise<void>;
  /**
   * Callback for deleting a tag (only for edit mode)
   */
  onDelete?: () => Promise<void>;
}

const TagManagementSchema = object({
  name: string({ required_error: "Name is required" })
    .trim()
    .min(1, "Name is required")
    .max(100, "Name limited to 100 characters"),
  color: string().trim().nullable().optional(),
});

/**
 * Component to create or update ItemTags.
 *
 * When a tag is provided, the component renders in 'editing' mode displaying a
 * delete button and using onUpdate/onDelete callbacks.
 *
 * When no tag is provided, the component is in 'creation' mode and displays a
 * '+' button to create a new tag using the onCreate callback.
 *
 * When the color picker is closed, the new color is automatically updated for existing tags.
 *
 * To update the tag name, enter the new name and press ENTER.
 */
export const TagListItem: FC<TagListItemProps> = (props) => {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const popoverRef = useRef(null);

  const { state, triggerProps, overlayProps } = useOverlay({});

  const [isLoadingDeleteTag, setLoadingDeleteTag] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const {
    handleSubmit,
    register,
    reset,
    setError,
    setFocus,
    setValue,
    watch,
    getValues,
    formState: { errors, isSubmitting, isDirty },
  } = useFormAria({
    defaultValues: { name: props.tag?.name ?? "", color: props.tag?.color ?? undefined },
    resolver: zodResolver(TagManagementSchema),
  });

  const onSubmit: SubmitHandler<z.infer<typeof TagManagementSchema>> = async (data) => {
    setIsLoading(true);
    const updateData = { name: data.name, colour: data.color };
    const handleError = (error: ApolloError | Error) => {
      setError("name", { message: error.message });
      setFocus("name");
    };
    if (props.tag && props.onUpdate) {
      await props.onUpdate(updateData).catch(handleError);
    } else if (!props.tag && props.onCreate) {
      await props.onCreate(updateData).catch(handleError);
      reset();
    }
    setIsLoading(false);
  };

  const handleDeleteTag = async () => {
    if (props.onDelete) {
      setLoadingDeleteTag(true);
      try {
        await props.onDelete();
        state.close();
      } catch (error) {
        setError("root", { message: (error as ApolloError | Error).message });
      }
      setLoadingDeleteTag(false);
    }
  };

  return (
    <form data-testid="edit_project_tag" onSubmit={handleSubmit(onSubmit)}>
      <VStack>
        <HStack>
          <ColorButton
            defaultColor={watch("color") ?? undefined}
            onApply={(color) => {
              setValue("color", color);
              if (props.tag && props.onUpdate) {
                // Auto-submit color changes for existing tags
                void handleSubmit(onSubmit)();
              }
            }}
          />
          <TextField
            style={{ flex: 1 }}
            placeholder="New tag name"
            {...register("name")}
            defaultValue={props.tag?.name}
            isRequired
            isInvalid={isDirty && !!errors.name}
            maxLength={100}
            onBlur={() => {
              // Auto-submit on blur for existing tags only
              if (isDirty && props.tag && props.onUpdate) {
                void handleSubmit(onSubmit)();
              }
            }}
          />
          <VisuallyHidden>
            {/* Just useful when the user presses "Enter" */}
            <button type="submit" disabled={isSubmitting} tabIndex={-1} />
          </VisuallyHidden>

          {props.tag ? (
            <TriggerButton
              icon={"delete"}
              label={""}
              key={"delete-tag"}
              aria-label="Remove tag"
              ref={triggerRef}
              {...triggerProps}
              isLoading={isLoading}
            />
          ) : (
            <TriggerButton
              icon={"add"}
              label={"Add New Tag"}
              key={"add-tag"}
              onPress={() => handleSubmit(onSubmit)()}
              isLoading={isLoading}
            />
          )}
          <Popover state={state} {...overlayProps} triggerRef={triggerRef} ref={popoverRef}>
            <AlertDialogComponent
              title="Remove Project Tag"
              mainButton={{
                icon: "delete",
                isLoading: isLoadingDeleteTag,
                label: "Remove",
                intent: ActionButtonIntent.Danger,
                onPress: handleDeleteTag,
              }}
              errorCallout={errors.root?.message ? { message: errors.root.message } : undefined}
              closeFn={() => {
                state.close();
              }}
            >
              Are you sure you want to remove &apos;{getValues("name")}&apos;?
            </AlertDialogComponent>
          </Popover>
        </HStack>
        {isDirty && !!errors.name && (
          <div role="alert" className={formStyle.error_message} data-testid="number-field-error">
            {errors.name?.message}
          </div>
        )}
      </VStack>
    </form>
  );
};
